import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { useAuth0 } from '@auth0/auth0-react';

import { useSageAuth } from 'src/auth/useSageAuth';
import datadogRum from 'src/datadog';
import { LogoutDialogTypes } from 'src/pages/LoginPage';
import { setBearerToken } from 'src/store/authSlice';

export default function useLogout() {
    const { logout: auth0Logout } = useAuth0();
    const session = useSageAuth();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const locationRef = useRef(location);
    // Callers await other work before logging out, and the owner can settle in between.
    const sessionRef = useRef(session);

    const clearLocalSession = useCallback(() => {
        datadogRum.clearAccount();
        datadogRum.clearUser();
        dispatch(setBearerToken(null));
    }, [dispatch]);

    useEffect(() => {
        // Keep a ref to the latest location so it can be accessed inside callbacks without needing to be a dependency.
        locationRef.current = location;
    }, [location]);

    useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    const logout = useCallback(
        (reason?: LogoutDialogTypes) => {
            clearLocalSession();
            return sessionRef.current.logout({
                returnTo: buildLogoutRedirectUrl(reason).toString(),
            });
        },
        [clearLocalSession]
    );

    const logoutWithoutAuth0Redirect = useCallback(
        async (reason?: LogoutDialogTypes) => {
            clearLocalSession();
            const { owner, logout: sessionLogout } = sessionRef.current;

            // Only the shell can end its own session. Its logout reloads into the redirect, so
            // there is no Auth0 redirect to avoid and nothing left to navigate.
            if (owner === 'native') {
                await sessionLogout({ returnTo: buildLogoutRedirectUrl(reason).toString() });
                return;
            }

            /**
             * Also clear auth0-react's cached session. The SDK only does this itself for
             * `login_required`, and while `isAuthenticated` is still true, SageApp redirects
             * /authenticate to /dashboard, so the user would never reach the login page. auth0-react
             * flips `isAuthenticated` once this resolves, so it has to finish before navigating.
             */
            try {
                await auth0Logout({ openUrl: false });
            } catch (error) {
                datadogRum.addError(error, { message: 'Failed to clear the local Auth0 session' });
            }

            const redirectUrl = buildLogoutRedirectUrl(reason);
            if (locationRef.current.pathname !== redirectUrl.pathname) {
                navigate(`${redirectUrl.pathname}${redirectUrl.search}`);
            }
        },
        [auth0Logout, clearLocalSession, navigate]
    );

    return { logout, logoutWithoutAuth0Redirect };
}

export function buildLogoutRedirectUrl(reason?: LogoutDialogTypes): URL {
    const redirectUrl = new URL('/authenticate', window.location.origin);

    if (reason) {
        redirectUrl.searchParams.set('logoutReason', reason);
    }
    return redirectUrl;
}
